import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

const ProgressBoard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const student = location.state?.student;

  // === Data States ===
  const [learningProgress, setLearningProgress] = useState([]);
  const [examResults, setExamResults] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // === Statistics ===
  const [stats, setStats] = useState({
    totalLessons: 0,
    completedLessons: 0,
    averageScore: 0,
    attendanceRate: 0,
    studyStreak: 0,
    totalAchievements: 0
  });

  useEffect(() => {
    if (!student) {
      navigate('/student-login');
      return;
    }

    loadAllProgressData();
  }, [student, navigate]);

  const loadAllProgressData = async () => {
    try {
      setLoading(true);

      // Load all progress data in parallel
      const [
        progressRes,
        examResultsRes,
        attendanceRes,
        achievementsRes
      ] = await Promise.all([
        fetch(`${API_ENDPOINTS.BASE_URL}/api/progress/${student.username}`),
        fetch(`${API_ENDPOINTS.BASE_URL}/api/exam/submissions/${student.username}`),
        fetch(`${API_ENDPOINTS.BASE_URL}/api/attendance/${student.username}`),
        fetch(`${API_ENDPOINTS.BASE_URL}/api/achievements/${student.username}`)
      ]);

      const progressData = await progressRes.json();
      const examData = await examResultsRes.json();
      const attendanceData = await attendanceRes.json();
      const achievementsData = await achievementsRes.json();

      setLearningProgress(progressData);
      setExamResults(examData);
      setAttendance(attendanceData);
      setAchievements(achievementsData);

      calculateStatistics(progressData, examData, attendanceData, achievementsData);

    } catch (err) {
      console.error('Error loading progress data:', err);
      setError('Failed to load progress data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const calculateStatistics = (progress, exams, attendance, achievements) => {
    // Learning progress stats
    const completedLessons = progress.filter(p => p.completed).length;
    const totalLessons = progress.length;

    // Exam scores
    const examScores = exams.map(e => e.percentage).filter(score => !isNaN(score));
    const averageScore = examScores.length > 0
      ? Math.round(examScores.reduce((a, b) => a + b, 0) / examScores.length)
      : 0;

    // Attendance rate
    const totalDays = attendance.length;
    const presentDays = attendance.filter(a => a.status === 'present').length;
    const attendanceRate = totalDays > 0 ? Math.round((presentDays / totalDays) * 100) : 0;

    // Study streak (consecutive days with progress)
    const studyStreak = calculateStudyStreak(progress);

    setStats({
      totalLessons,
      completedLessons,
      averageScore,
      attendanceRate,
      studyStreak,
      totalAchievements: achievements.length
    });
  };

  const calculateStudyStreak = (progress) => {
    if (progress.length === 0) return 0;

    // Sort progress by date
    const sortedProgress = progress
      .filter(p => p.completed)
      .sort((a, b) => new Date(b.completedAt || b.createdAt) - new Date(a.completedAt || a.createdAt));

    let streak = 0;
    let currentDate = new Date();

    for (const item of sortedProgress) {
      const itemDate = new Date(item.completedAt || item.createdAt);
      const daysDiff = Math.floor((currentDate - itemDate) / (1000 * 60 * 60 * 24));

      if (daysDiff === streak) {
        streak++;
      } else if (daysDiff > streak) {
        break; // Gap in streak
      }
    }

    return streak;
  };

  const getProgressPercentage = () => {
    return stats.totalLessons > 0 ? Math.round((stats.completedLessons / stats.totalLessons) * 100) : 0;
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreGrade = (score) => {
    if (score >= 80) return 'A';
    if (score >= 70) return 'B';
    if (score >= 60) return 'C';
    if (score >= 50) return 'D';
    return 'F';
  };

  const getAttendanceColor = (rate) => {
    if (rate >= 90) return 'text-green-600';
    if (rate >= 75) return 'text-blue-600';
    if (rate >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (!student) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-blue-50 flex items-center justify-center">
        <div className="text-xl text-blue-600">Loading your progress...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-blue-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center p-8 bg-white rounded-lg shadow">
          <div className="text-6xl mb-4">Warning</div>
          <h2 className="text-xl font-bold text-gray-900 mb-4">Progress Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => {
              setError(null);
              loadAllProgressData();
            }}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Progress Board</h1>
              <p className="text-sm text-gray-600">Track your academic journey and achievements</p>
            </div>
            <button
              onClick={() => navigate('/student-dashboard', { state: { student } })}
              className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Welcome back, {student.firstName}! 👋
              </h2>
              <p className="text-gray-600 mt-1">
                Here's your academic progress overview
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-600">
                {getProgressPercentage()}%
              </div>
              <div className="text-sm text-gray-500">Overall Progress</div>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-blue-600">{stats.completedLessons}</div>
                <div className="text-sm text-gray-600">Lessons Completed</div>
              </div>
              <div className="text-3xl">Books</div>
            </div>
            <div className="mt-4">
              <div className="bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${getProgressPercentage()}%` }}
                ></div>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {stats.completedLessons} of {stats.totalLessons} lessons
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className={`text-2xl font-bold ${getScoreColor(stats.averageScore)}`}>
                  {stats.averageScore}%
                </div>
                <div className="text-sm text-gray-600">Average Score</div>
              </div>
              <div className="text-3xl">Stats</div>
            </div>
            <div className="mt-4">
              <div className={`text-sm font-medium ${getScoreColor(stats.averageScore)}`}>
                Grade: {getScoreGrade(stats.averageScore)}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className={`text-2xl font-bold ${getAttendanceColor(stats.attendanceRate)}`}>
                  {stats.attendanceRate}%
                </div>
                <div className="text-sm text-gray-600">Attendance Rate</div>
              </div>
              <div className="text-3xl">📅</div>
            </div>
            <div className="mt-4">
              <div className="text-xs text-gray-500">
                {attendance.filter(a => a.status === 'present').length} of {attendance.length} days
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-purple-600">{stats.studyStreak}</div>
                <div className="text-sm text-gray-600">Day Streak</div>
              </div>
              <div className="text-3xl">🔥</div>
            </div>
            <div className="mt-4">
              <div className="text-xs text-gray-500">
                Keep learning daily!
              </div>
            </div>
          </div>
        </div>

        {/* Progress Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Learning Progress */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Books Learning Progress</h3>
              <p className="text-gray-600 text-sm">Your lesson completion status</p>
            </div>
            <div className="p-6">
              {learningProgress.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-4xl mb-4">Study</div>
                  <p className="text-gray-600">No learning progress yet</p>
                  <p className="text-sm text-gray-500 mt-2">Start learning to see your progress here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {learningProgress.slice(0, 5).map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className={`w-3 h-3 rounded-full ${
                          item.completed ? 'bg-green-500' : 'bg-blue-500'
                        }`}></div>
                        <div>
                          <div className="font-medium text-gray-900">{item.subject}</div>
                          <div className="text-sm text-gray-600">{item.topic || 'General'}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-sm font-medium ${
                          item.completed ? 'text-green-600' : 'text-blue-600'
                        }`}>
                          {item.completed ? 'Completed' : 'In Progress'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(item.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))}
                  {learningProgress.length > 5 && (
                    <div className="text-center text-gray-600 text-sm">
                      +{learningProgress.length - 5} more lessons
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Exam Results */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Note Exam Results</h3>
              <p className="text-gray-600 text-sm">Your midterm and final exam scores</p>
            </div>
            <div className="p-6">
              {examResults.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-4xl mb-4">Stats</div>
                  <p className="text-gray-600">No exam results yet</p>
                  <p className="text-sm text-gray-500 mt-2">Take exams to see your results here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {examResults.slice(0, 3).map((exam, index) => (
                    <div key={index} className="p-4 border rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="font-medium text-gray-900">{exam.examTitle}</div>
                          <div className="text-sm text-gray-600">{exam.examType} Exam</div>
                        </div>
                        <div className="text-right">
                          <div className={`text-lg font-bold ${getScoreColor(exam.percentage)}`}>
                            {exam.percentage}%
                          </div>
                          <div className={`text-xs ${getScoreColor(exam.percentage)}`}>
                            Grade: {getScoreGrade(exam.percentage)}
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>Score: {exam.score}/{exam.maxScore}</span>
                        <span>{new Date(exam.submittedAt).toLocaleDateString()}</span>
                      </div>
                      {exam.autoSubmitted && (
                        <div className="mt-2 text-xs text-orange-600">
                          Warning Auto-submitted (time expired)
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Attendance Overview */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">📅 Attendance Overview</h3>
              <p className="text-gray-600 text-sm">Your class attendance record</p>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {attendance.filter(a => a.status === 'present').length}
                  </div>
                  <div className="text-sm text-gray-600">Present</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">
                    {attendance.filter(a => a.status === 'absent').length}
                  </div>
                  <div className="text-sm text-gray-600">Absent</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-600">
                    {attendance.filter(a => a.status === 'late').length}
                  </div>
                  <div className="text-sm text-gray-600">Late</div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className={`${getAttendanceColor(stats.attendanceRate)} font-medium`}>
                    Overall Attendance: {stats.attendanceRate}%
                  </span>
                  <span className="text-gray-600">
                    {attendance.length} total days
                  </span>
                </div>
                <div className="bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${
                      stats.attendanceRate >= 90 ? 'bg-green-600' :
                      stats.attendanceRate >= 75 ? 'bg-blue-600' :
                      stats.attendanceRate >= 60 ? 'bg-yellow-600' : 'bg-red-600'
                    }`}
                    style={{ width: `${stats.attendanceRate}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Achievements */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Award Achievements</h3>
              <p className="text-gray-600 text-sm">Your academic accomplishments</p>
            </div>
            <div className="p-6">
              {achievements.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-4xl mb-4">🎖️</div>
                  <p className="text-gray-600">No achievements yet</p>
                  <p className="text-sm text-gray-500 mt-2">Complete lessons and exams to earn achievements</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {achievements.slice(0, 3).map((achievement, index) => (
                    <div key={index} className="flex items-center space-x-3 p-3 border rounded-lg">
                      <div className="text-2xl">Award</div>
                      <div>
                        <div className="font-medium text-gray-900">{achievement.title}</div>
                        <div className="text-sm text-gray-600">{achievement.description}</div>
                        <div className="text-xs text-gray-500">
                          {new Date(achievement.date).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow mt-8">
          <div className="p-6 border-b">
            <h3 className="text-lg font-semibold text-gray-900">Growth Recent Activity</h3>
            <p className="text-gray-600 text-sm">Your latest academic activities</p>
          </div>
          <div className="p-6">
            {[...learningProgress, ...examResults]
              .sort((a, b) => new Date(b.createdAt || b.submittedAt) - new Date(a.createdAt || a.submittedAt))
              .slice(0, 5)
              .map((activity, index) => (
                <div key={index} className="flex items-center space-x-3 py-3 border-b border-gray-100 last:border-b-0">
                  <div className="text-lg">
                    {activity.examType ? 'Note' : activity.completed ? '?' : 'Study'}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">
                      {activity.examType
                        ? `Completed ${activity.examType} Exam: ${activity.examTitle}`
                        : activity.completed
                        ? `Completed lesson: ${activity.subject}`
                        : `Started lesson: ${activity.subject}`
                      }
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(activity.createdAt || activity.submittedAt).toLocaleString()}
                    </div>
                  </div>
                  {activity.percentage && (
                    <div className={`text-sm font-medium ${getScoreColor(activity.percentage)}`}>
                      {activity.percentage}%
                    </div>
                  )}
                </div>
              ))}
            {[...learningProgress, ...examResults].length === 0 && (
              <div className="text-center py-8 text-gray-600">
                No recent activity. Start learning to see your progress here!
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressBoard;



